/* ==========================================================================
   FLC European Living — Custom Scripts
   All theme customizations go here.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Variant Price Update (for product detail pages with compare price)
   -------------------------------------------------------------------------- */

async function flcHandleArticleShare(button) {
  if (!(button instanceof HTMLElement)) return;

  const shareTitle = button.dataset.shareTitle || '';
  const shareUrl = button.dataset.shareUrl || window.location.href;

  try {
    if (navigator.share) {
      await navigator.share({ title: shareTitle, url: shareUrl });
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
    }
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.error('Share failed:', error);
    }
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-article-share-button]') : null;
  if (!target) return;
  event.preventDefault();
  flcHandleArticleShare(target);
});

// Listen for variant changes on detail pages and update compare price
document.addEventListener('flc:variant:changed', (e) => {
  const { variant } = e.detail;
  if (!variant) return;

  // Update compare price display if it exists
  const comparePriceEl = document.querySelector('[data-product-compare-price]');
  if (comparePriceEl && variant.compare_at_price > variant.price) {
    comparePriceEl.innerHTML = FLC?.formatMoneyNoCents?.(variant.compare_at_price) || 
      new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(variant.compare_at_price / 100);
    comparePriceEl.closest('[data-price-wrap]')?.classList.add('price--on-sale');
  } else if (comparePriceEl) {
    comparePriceEl.innerHTML = '';
    comparePriceEl.closest('[data-price-wrap]')?.classList.remove('price--on-sale');
  }
});

/* --------------------------------------------------------------------------
   Variant-Specific Accordion Content Swap
   For multi-variant products, accordion sections (dimensions, model details,
   package) are pre-rendered per variant and toggled on variant change.
   Items where the current variant has no content are hidden entirely.
   -------------------------------------------------------------------------- */
function flcCollapseAccordionItem(item) {
  if (!item) return;
  const trigger = item.querySelector('[data-accordion-trigger]');
  const content = item.querySelector('[data-accordion-content]');
  if (trigger) trigger.setAttribute('aria-expanded', 'false');
  if (content) {
    content.style.height = '';
    content.style.overflow = '';
    content.hidden = true;
  }
}

document.addEventListener('flc:variant:changed', (e) => {
  const { variant } = e.detail;
  if (!variant) return;

  const variantId = String(variant.id);

  document.querySelectorAll('[data-variant-accordion]').forEach((wrap) => {
    let activeHasContent = false;
    wrap.querySelectorAll(':scope > [data-variant-content]').forEach((node) => {
      const matches = node.dataset.variantContent === variantId;
      node.hidden = !matches;
      if (matches && node.dataset.hasContent === 'true') {
        activeHasContent = true;
      }
    });

    // Find the parent accordion item and toggle visibility
    const item = wrap.closest('[data-accordion-item]');
    if (item) {
      if (activeHasContent) {
        item.hidden = false;
        item.style.display = '';
      } else {
        // Hide entire accordion item; collapse first if it was open
        flcCollapseAccordionItem(item);
        item.hidden = true;
        item.style.display = 'none';
      }
    }
  });
});

// Run once on load to hide accordion items where the current variant has no content
function flcInitVariantAccordions() {
  document.querySelectorAll('[data-variant-accordion]').forEach((wrap) => {
    const visible = wrap.querySelector(':scope > [data-variant-content]:not([hidden])');
    const item = wrap.closest('[data-accordion-item]');
    if (!item) return;
    if (visible && visible.dataset.hasContent === 'true') {
      item.hidden = false;
      item.style.display = '';
    } else {
      item.hidden = true;
      item.style.display = 'none';
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', flcInitVariantAccordions);
} else {
  flcInitVariantAccordions();
}

/* --------------------------------------------------------------------------
   Product Card — Sibling Swatch Swap
   Hover  → image only (preview on mouseenter, restore on mouseleave)
   Click  → commit: active swatch, title, price, all link hrefs
            OR navigate to product page if SIBLING_CLICK_COMMIT is false

   Feature flags (set to false to disable without removing the logic):
     SIBLING_HOVER_IMAGE   — hover over a swatch previews that sibling's image
     SIBLING_CLICK_COMMIT  — clicking a swatch commits title/price/links in-card
                             (when false, clicking navigates to the product page)
   -------------------------------------------------------------------------- */
(function () {
  // ── Feature flags ──────────────────────────────────────────────────────────
  const SIBLING_HOVER_IMAGE  = true;  // hover over a swatch previews that sibling's image
  const SIBLING_CLICK_COMMIT = false; // set true to re-enable in-card commit on click
  // ──────────────────────────────────────────────────────────────────────────

  const buildSrcset = (url600) => {
    const widths = [180, 300, 400, 600];
    return widths.map((w) => `${url600.replace('_600x', `_${w}x`)} ${w}w`).join(', ');
  };

  const HOVER_DELAY = 100;
  const FADE_MS = 220;

  // True crossfade: clone current img on top → swap real src → fade clone out
  const fadeSwap = (img, src, alt) => {
    if (!img) return () => {};
    let cancelled = false;
    let clone = null;

    const preload = new Image();
    preload.onload = preload.onerror = () => {
      if (cancelled) return;

      // Clone the current image and overlay it exactly
      clone = img.cloneNode(true);
      const parent = img.parentElement;
      clone.style.cssText = `
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
        z-index: 2;
        transition: opacity ${FADE_MS}ms ease;
        opacity: 1;
      `;
      parent.style.position = parent.style.position || 'relative';
      parent.appendChild(clone);

      // Swap the real image underneath (already preloaded — no blank)
      img.src = src;
      img.srcset = buildSrcset(src);
      img.alt = alt;
      img.title = alt;

      // Fade the clone out, then remove it
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) { clone?.remove(); clone = null; return; }
          clone.style.opacity = '0';
          clone.addEventListener('transitionend', () => { clone?.remove(); clone = null; }, { once: true });
        });
      });
    };
    preload.src = src;

    return () => { cancelled = true; clone?.remove(); clone = null; };
  };

  const snapSwap = (img, src, alt) => {
    if (!img) return;
    img.src = src;
    img.srcset = buildSrcset(src);
    img.alt = alt;
    img.title = alt;
  };

  const setCardImage = (card, src, alt, smooth) => {
    if (card._pcCancelImage) { card._pcCancelImage(); card._pcCancelImage = null; }
    const main = card.querySelector('.product-card__img:not(.product-card__img--hover)');
    const hover = card.querySelector('.product-card__img--hover');
    const slide = card.querySelector('.product-card__carousel-slide:first-child img');
    if (smooth) {
      const cancels = [
        fadeSwap(main, src, alt),
        fadeSwap(hover, src, alt),
        slide ? fadeSwap(slide, src, alt) : () => {},
      ];
      card._pcCancelImage = () => cancels.forEach((fn) => fn());
    } else {
      snapSwap(main, src, alt);
      if (hover) snapSwap(hover, src, alt);
      if (slide) snapSwap(slide, src, alt);
    }
  };

  const getOriginalImage = (card) => {
    if (!card._pcOriginalImage) {
      const img = card.querySelector('.product-card__img:not(.product-card__img--hover)');
      const hoverImg = card.querySelector('.product-card__img--hover');
      if (img) {
        card._pcOriginalImage = {
          src: img.src, srcset: img.srcset, alt: img.alt,
          hoverSrc: hoverImg?.src ?? null,
          hoverSrcset: hoverImg?.srcset ?? null,
          hoverAlt: hoverImg?.alt ?? null,
        };
      }
    }
    return card._pcOriginalImage;
  };

  // --- Hover: debounced image preview (requires SIBLING_HOVER_IMAGE = true) ---
  if (SIBLING_HOVER_IMAGE) {
    const restoreCard = (card) => {
      clearTimeout(card._pcHoverTimer);
      if (card._pcCancelImage) { card._pcCancelImage(); card._pcCancelImage = null; }
      const orig = getOriginalImage(card);
      if (!orig) return;
      const mainImg = card.querySelector('.product-card__img:not(.product-card__img--hover)');
      if (mainImg) { mainImg.src = orig.src; mainImg.srcset = orig.srcset; mainImg.alt = orig.alt; mainImg.title = orig.alt; }
      const hoverImg = card.querySelector('.product-card__img--hover');
      if (hoverImg && orig.hoverSrc) { hoverImg.src = orig.hoverSrc; hoverImg.srcset = orig.hoverSrcset; hoverImg.alt = orig.hoverAlt; hoverImg.title = orig.hoverAlt; }
    };

    document.addEventListener('mouseenter', (e) => {
      if (!(e.target instanceof Element)) return;
      const swatch = e.target.closest('.product-card__sibling-swatch');
      if (!swatch) return;
      const card = swatch.closest('[data-pc-sibling-card]');
      if (!card) return;
      const image = swatch.dataset.siblingImage;
      if (!image) return;

      getOriginalImage(card); // capture before first swap

      clearTimeout(card._pcHoverTimer);
      card._pcHoverTimer = setTimeout(() => {
        const alt = swatch.dataset.siblingImageAlt || swatch.dataset.siblingTitle || '';
        setCardImage(card, image, alt, true);
      }, HOVER_DELAY);
    }, true);

    // Restore when leaving a swatch — so moving back to the image area re-enables the hover effect
    document.addEventListener('mouseleave', (e) => {
      if (!(e.target instanceof Element)) return;
      const swatch = e.target.closest('.product-card__sibling-swatch');
      if (!swatch) return;
      const card = swatch.closest('[data-pc-sibling-card]');
      if (!card) return;
      restoreCard(card);
    }, true);

    // Also restore when mouse fully exits the card (covers fast movements that skip swatch leave)
    document.addEventListener('mouseout', (e) => {
      if (!(e.target instanceof Element)) return;
      const card = e.target.closest('[data-pc-sibling-card]');
      if (!card) return;
      if (card.contains(e.relatedTarget)) return; // still inside the card
      restoreCard(card);
    });
  }

  // --- Click: commit title, price, links (requires SIBLING_CLICK_COMMIT = true)
  //     When SIBLING_CLICK_COMMIT is false, clicks navigate to the product page. ---
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.product-card__sibling-swatch');
    if (!swatch) return;
    const card = swatch.closest('[data-pc-sibling-card]');
    if (!card) return;

    const url = swatch.dataset.siblingUrl;
    if (!url) return;

    e.preventDefault();
    clearTimeout(card._pcHoverTimer);

    if (!SIBLING_CLICK_COMMIT) {
      window.location.href = url;
      return;
    }

    const title = swatch.dataset.siblingTitle;
    const price = swatch.dataset.siblingPrice;
    const comparePrice = swatch.dataset.siblingComparePrice;
    const image = swatch.dataset.siblingImage;
    const imageAlt = swatch.dataset.siblingImageAlt || title;

    // Update active swatch
    card.querySelectorAll('.product-card__sibling-swatch').forEach((s) => {
      s.classList.remove('is-active');
      s.removeAttribute('aria-current');
    });
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-current', 'true');

    // Commit image (already visible from hover; instant swap avoids double-fade)
    if (image) {
      setCardImage(card, image, imageAlt, false);
      const mediaLink = card.querySelector('.product-card__img:not(.product-card__img--hover)')?.closest('a');
      if (mediaLink) mediaLink.href = url;
    }

    // Title
    if (title) {
      const titleLink = card.querySelector('.product-card__title a');
      if (titleLink) { titleLink.textContent = title; titleLink.href = url; }
    }

    // All other card links
    card.querySelectorAll('a[href]').forEach((a) => {
      if (a.classList.contains('product-card__sibling-swatch')) return;
      a.href = url;
    });

    // Price
    if (price) {
      const priceEl = card.querySelector('.price__current');
      if (priceEl) priceEl.textContent = price;
      const priceWrap = card.querySelector('.product-card__price');
      const compareEl = card.querySelector('.price__compare');
      if (comparePrice) {
        if (priceWrap) priceWrap.classList.add('price--on-sale');
        if (compareEl) { compareEl.textContent = comparePrice; compareEl.hidden = false; }
        else if (priceWrap) {
          const s = document.createElement('s');
          s.className = 'price__compare';
          s.textContent = comparePrice;
          priceWrap.appendChild(s);
        }
      } else {
        if (priceWrap) priceWrap.classList.remove('price--on-sale');
        if (compareEl) compareEl.hidden = true;
      }
    }
  });
}());

/* --------------------------------------------------------------------------
   Search results page — refine search input clear button
   -------------------------------------------------------------------------- */
(function () {
  function init(form) {
    if (!form || form.dataset.searchPageInit === 'true') return;
    form.dataset.searchPageInit = 'true';

    var input = form.querySelector('[data-search-page-input]');
    var clearBtn = form.querySelector('[data-search-page-clear]');
    if (!input || !clearBtn) return;

    function sync() {
      if (input.value && input.value.length > 0) {
        form.classList.add('has-value');
      } else {
        form.classList.remove('has-value');
      }
    }

    sync();
    input.addEventListener('input', sync);
    clearBtn.addEventListener('click', function () {
      input.value = '';
      sync();
      input.focus();
    });
  }

  document.querySelectorAll('.search-page-form').forEach(init);
}());

/* --------------------------------------------------------------------------
   Instafeed — Mobile slider initializer
   The instafeed app renders its DOM asynchronously and may also populate
   images/content after the container itself appears. We wait until at least
   one .instafeed-new-layout-wrapper has a child element (i.e. actual content
   has been injected) before applying slider styles.
   -------------------------------------------------------------------------- */
(function () {
  const MOBILE_BP = 749;
  const MAX_ATTEMPTS = 40;   // up to ~8 s total
  const INTERVAL_MS  = 200;

  function hasContent() {
    const wrapper = document.querySelector('.instafeed-new-layout-wrapper');
    return wrapper && wrapper.children.length > 0;
  }

  function applyInstafeedSlider() {
    if (window.innerWidth > MOBILE_BP) return;

    const container = document.querySelector('.instafeed-new-layout-container');
    if (!container) return;

    const wrapper = container.querySelector('.instafeed-new-layout-wrapper');
    if (!wrapper) return;

    // Override the container's hardcoded pixel width
    container.style.setProperty('width', '100%', 'important');
    container.style.setProperty('overflow', 'hidden', 'important');

    // Convert the wrapper from the app's grid layout to a flex scroll track
    wrapper.style.setProperty('display', 'flex', 'important');
    wrapper.style.setProperty('flex-wrap', 'nowrap', 'important');
    wrapper.style.setProperty('grid-template-columns', 'unset', 'important');
    wrapper.style.setProperty('width', '100%', 'important');
    wrapper.style.setProperty('overflow-x', 'auto', 'important');
    wrapper.style.setProperty('overflow-y', 'hidden', 'important');
    wrapper.style.setProperty('scroll-snap-type', 'x mandatory', 'important');
    wrapper.style.setProperty('scrollbar-width', 'none', 'important');
    wrapper.style.setProperty('-ms-overflow-style', 'none', 'important');
    wrapper.style.setProperty('padding-left', '20px', 'important');
    wrapper.style.setProperty('padding-right', '20px', 'important');
    wrapper.style.setProperty('scroll-padding-left', '20px', 'important');
    wrapper.style.setProperty('box-sizing', 'border-box', 'important');
    wrapper.style.setProperty('gap', '10px', 'important');

    // Size each item as a slide (2 visible + peek of 3rd)
    const slideWidth = 'calc((100vw - 20px - 10px) / 2.25)';
    wrapper.querySelectorAll('.instafeed-new-layout-item').forEach(function (item) {
      item.style.setProperty('flex', '0 0 ' + slideWidth, 'important');
      item.style.setProperty('width', slideWidth, 'important');
      item.style.setProperty('height', 'auto', 'important');
      item.style.setProperty('clip-path', 'none', 'important');
      item.style.setProperty('scroll-snap-align', 'start', 'important');
    });
  }

  function waitForContent() {
    if (window.innerWidth > MOBILE_BP) return;

    let attempts = 0;

    const interval = setInterval(function () {
      attempts++;

      if (hasContent()) {
        clearInterval(interval);
        applyInstafeedSlider();
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval);
      }
    }, INTERVAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForContent);
  } else {
    waitForContent();
  }
}());

/* Prevent iOS Safari horizontal overscroll bounce on carousel tracks
   iOS Safari ignores overscroll-behavior-x, so we block touchmove when the
   user is at a scroll boundary and trying to scroll further horizontally.
   This stops the rubber-band effect that causes white-space gaps. */
(function () {
  var startX, startY;

  document.addEventListener('touchstart', function (e) {
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    var track = e.target.closest('.hover-showcase__track, .featured-col__track, .col-subcats__track, .cart-upsell__track');
    if (!track) return;

    var dx = e.touches[0].clientX - startX;
    var dy = e.touches[0].clientY - startY;

    // Only intercept clearly horizontal swipes
    if (Math.abs(dx) <= Math.abs(dy)) return;

    var atStart = track.scrollLeft <= 0;
    var atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

    if ((atStart && dx > 0) || (atEnd && dx < 0)) {
      e.preventDefault();
    }
  }, { passive: false });
}());

/* Custom Handler to Refresh and Open Mini-cart in the Wishlist */
document.addEventListener("DOMContentLoaded", () => {
  const wishlistBodyNode = document.querySelector("body");
  wishlistBodyNode.addEventListener("wishlistAddToCartEvent", (e) => {
    document.documentElement.dispatchEvent(new CustomEvent('product:added', {
      bubbles: true,
      detail: {
        quantity: 1
      }
    }));
  });
});

/* DEV-02: alt-text fallback for merchant rich text (.rte)
   Product/collection descriptions and page/article bodies render as raw HTML
   from the Shopify admin editor, so Liquid's image snippets (which already
   fall back to product/section title) never touch them. This patches any
   <img> left without alt so accessibility tools always get something. */
document.addEventListener('DOMContentLoaded', () => {
  const heading = document.querySelector('h1');
  const fallbackAlt = (heading && heading.textContent.trim()) || document.title;

  document.querySelectorAll('.rte img').forEach((img) => {
    if (img.getAttribute('alt')) return;

    const filenameAlt = (img.getAttribute('src') || '')
      .split('/').pop()
      .split('?')[0]
      .split('.')[0]
      .replace(/[-_]/g, ' ')
      .trim();

    img.setAttribute('alt', fallbackAlt || filenameAlt);
  });
});